import { User, ProfileForm, APIResponse, ProfileCompletionStatus } from '@/types';
import { apiClient } from './api-client';

export class ProfileService {
  /**
   * Get current user profile
   */
  static async getProfile(): Promise<User> {
    try {
      console.log('ProfileService: Attempting to fetch profile...');
      const response = await apiClient.get<User>('/profile/me');
      console.log('ProfileService: Profile fetched successfully');
      return response.data;
    } catch (error: any) {
      console.error('ProfileService: Failed to fetch profile:', error);
      if (error?.status === 401) {
        console.error('ProfileService: Authorization failed - missing or invalid token');
      }
      throw error;
    }
  }

  /**
   * Update user profile
   */
  static async updateProfile(profileData: ProfileForm): Promise<User> {
    const response = await apiClient.put<User>('/profile/me', profileData);
    return response.data;
  }

  /**
   * Get presigned URL for photo upload
   */
  static async getPhotoUploadUrl(fileName: string, fileType: string, fileSize: number): Promise<{ uploadUrl: string; photoUrl: string; uploadId: string }> {
    const response = await apiClient.post<{ uploadUrl: string; photoUrl: string; uploadId: string }>('/profile/upload-photo', {
      fileName,
      fileType,
      fileSize,
    });
    return response.data;
  }

  /**
   * Upload photo to S3 using presigned URL
   */
  static async uploadPhoto(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }
  }

  /**
   * Delete profile photo
   */
  static async deletePhoto(): Promise<void> {
    await apiClient.delete('/profile/photo');
  }

  /**
   * Calculate profile completion status
   */
  static calculateProfileCompletion(user: User): ProfileCompletionStatus {
    const requiredFields = ['name', 'bio', 'photoUrl'];
    const completedFields: string[] = [];
    const missingFields: string[] = [];

    requiredFields.forEach(field => {
      const value = user[field as keyof User];
      if (value && value.toString().trim() !== '') {
        completedFields.push(field);
      } else {
        missingFields.push(field);
      }
    });

    const completionPercentage = Math.round((completedFields.length / requiredFields.length) * 100);
    const isComplete = completionPercentage === 100;

    return {
      isComplete,
      completedFields,
      missingFields,
      completionPercentage,
    };
  }

  /**
   * Get field display names for UI
   */
  static getFieldDisplayName(fieldName: string): string {
    const fieldNames: Record<string, string> = {
      name: 'Full Name',
      bio: 'Bio',
      photoUrl: 'Profile Photo',
    };

    return fieldNames[fieldName] || fieldName;
  }
}

export default ProfileService;